import { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Paper, Stack } from '@mui/material';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';
import { UserType } from '../../../interfaces/userType.enum';
import { DashboardService } from '../../../services/dashboard.service';
import { getWidget, isWidgetVisibleToRole, ModuleKey } from '../registry';
import { isGatedModule } from '../registry/moduleMeta';
import { CURRENCY_SYMBOL, fmtMoney } from '../registry/format';
import { compactMoney, formatPeriodLabel } from '../charts/axisPadding';
import StatTile, { StatTone } from '../charts/StatTile';
import { useDashboardAccess } from '../useDashboardAccess';

/**
 * Headline KPI strip shown at the top of the home board and of every module board: one outlined
 * Paper holding a wrapping row of StatTiles.
 *
 * Every tile comes from a DashboardService endpoint that the matching board widgets already use,
 * and a module's tiles are only requested when the caller could also see that module's own
 * summary widget (same role gate + enabled-module gate the boards apply), so the strip never
 * causes a 403 the board would not. A tile whose call fails is simply omitted.
 */

export interface KpiTile {
    key: string;
    label: string;
    value: string | number;
    unit?: string;
    sub?: string;
    tone?: StatTone;
}

interface Built { tile: KpiTile; home?: boolean }

/** Which registered widget's visibility decides whether the module's numbers may be requested. */
const GATE_WIDGET: Partial<Record<ModuleKey, string>> = {
    attendance: 'attendance.dayPulse',
    leave: 'leave.summary',
    people: 'people.summary',
    payroll: 'payroll.summary',
};

/** Home board: tile keys in display order; only tiles flagged `home` by their module are kept. */
const HOME_ORDER = ['att.present', 'att.absent', 'pay.net', 'leave.pending', 'people.active'];
const HOME_MAX = 10;

/**
 * The People board is the workforce overview (attendance, leave and headcount widgets), so its
 * strip spans those three modules too; each module's tiles still only load when the caller could
 * see that module's own widgets.
 */
const PEOPLE_MODULES: ModuleKey[] = ['attendance', 'leave', 'people'];
const PEOPLE_ORDER = [
    'att.present', 'att.absent', 'att.leave', 'att.rate', 'leave.pending',
    'people.active', 'people.inactive', 'people.noDept', 'people.departments',
];

export const HOME_MODULES: ModuleKey[] = ['attendance', 'payroll', 'leave', 'people'];

/** The label the backend gives members without a department in the people summary. */
const NO_DEPARTMENT_BUCKET = 'No department';

type Loader = (institutionId: string, t: TFunction, full: boolean, compact: boolean) => Promise<Built[]>;

const settled = async <T,>(p: () => Promise<T>): Promise<T | null> => {
    try { return await p(); } catch { return null; }
};

const money = (n: number, compact: boolean) => (compact ? compactMoney(n) : fmtMoney(n));

const LOADERS: Partial<Record<ModuleKey, Loader>> = {
    attendance: async (id, t) => {
        const d = await DashboardService.getAttendanceSummary(id);
        const rate = d.totalUsers > 0 ? Math.round((d.presentToday / d.totalUsers) * 100) : null;
        const out: Built[] = [
            { home: true, tile: { key: 'att.present', label: t('kpiStrip.presentToday'), value: d.presentToday, sub: t('widgets.ofTotal', { total: d.totalUsers }), tone: 'good' } },
            { tile: { key: 'att.leave', label: t('kpiStrip.onLeaveToday'), value: d.onLeaveToday } },
            {
                home: true,
                tile: {
                    key: 'att.absent', label: t('kpiStrip.absentToday'), value: d.absentToday,
                    tone: d.absentToday > 0 ? 'warning' : 'default',
                    sub: d.onLeaveToday > 0 ? t('kpiStrip.nOnLeave', { n: d.onLeaveToday }) : undefined,
                },
            },
        ];
        if (rate != null) out.push({ tile: { key: 'att.rate', label: t('kpiStrip.attendanceToday'), value: `${rate}%`, sub: t('kpiStrip.presentOfAll') } });
        return out;
    },
    leave: async (id, t) => {
        const d = await DashboardService.getLeaveSummary(id);
        return [
            { home: true, tile: { key: 'leave.pending', label: t('kpiStrip.pendingLeave'), value: d.pending, tone: d.pending > 0 ? 'warning' : 'default' } },
            { tile: { key: 'leave.approved', label: t('kpiStrip.approved'), value: d.approved, tone: 'good', sub: t('kpiStrip.allApplications') } },
            { tile: { key: 'leave.rejected', label: t('kpiStrip.rejected'), value: d.rejected, sub: t('kpiStrip.allApplications') } },
        ];
    },
    people: async (id, t) => {
        const d = await DashboardService.getPeopleSummary(id);
        const departments = d.byDepartment.filter(b => b.label !== NO_DEPARTMENT_BUCKET && b.count > 0).length;
        return [
            { home: true, tile: { key: 'people.active', label: t('kpiStrip.activeHeadcount'), value: d.active, sub: t('kpiStrip.ofMembers', { total: d.total }), tone: 'good' } },
            { tile: { key: 'people.inactive', label: t('kpiStrip.inactive'), value: d.inactive } },
            { tile: { key: 'people.noDept', label: t('kpiStrip.noDepartment'), value: d.noDepartmentCount, tone: d.noDepartmentCount > 0 ? 'warning' : 'default', sub: t('kpiStrip.activeMembers') } },
            { tile: { key: 'people.departments', label: t('kpiStrip.departments'), value: departments, sub: t('kpiStrip.withActiveMembers') } },
        ];
    },
    payroll: async (id, t, full, compact) => {
        const [d, overtime] = await Promise.all([
            DashboardService.getPayrollSummary(id),
            full ? settled(() => DashboardService.getPayrollOvertime(id)) : Promise.resolve(null),
        ]);
        if (d.lastRunPeriod == null) return [];
        const period = formatPeriodLabel(d.lastRunPeriod);
        const out: Built[] = [
            { home: true, tile: { key: 'pay.net', label: t('kpiStrip.lastPayrollNet'), unit: CURRENCY_SYMBOL, value: money(d.lastRunTotal, compact), sub: period } },
            { tile: { key: 'pay.headcount', label: t('kpiStrip.employeesPaid'), value: d.headcount, sub: period } },
        ];
        if (d.headcount > 0) {
            out.push({ tile: { key: 'pay.avg', label: t('kpiStrip.avgNetPay'), unit: CURRENCY_SYMBOL, value: money(d.lastRunTotal / d.headcount, compact), sub: t('kpiStrip.perEmployeeLastRun') } });
        }
        if (overtime && overtime.length > 0) {
            const last = overtime[overtime.length - 1];
            out.push({ tile: { key: 'pay.ot', label: t('kpiStrip.overtimeHours'), value: Number.isInteger(last.hours) ? last.hours : last.hours.toFixed(1), sub: formatPeriodLabel(last.month) } });
        }
        return out;
    },
};

interface Props {
    institutionId: string;
    /** `home` = cross-module headline tiles; a module key = that module's own KPIs. */
    scope: 'home' | ModuleKey;
}

export default function BoardKpiStrip({ institutionId, scope }: Props) {
    const { t, i18n } = useTranslation('dashboard');
    const { user } = useAuth();
    const roles: UserType[] = useMemo(() => {
        const r = user?.roles;
        return (Array.isArray(r) ? r : r ? [r] : []) as UserType[];
    }, [user]);
    const { enabledModules } = useDashboardAccess();

    const canSee = (m: ModuleKey): boolean => {
        const w = getWidget(GATE_WIDGET[m] ?? '');
        return !!w && (!isGatedModule(m) || enabledModules.has(m)) && isWidgetVisibleToRole(w, roles);
    };

    const candidates = scope === 'home' ? HOME_MODULES : scope === 'people' ? PEOPLE_MODULES : [scope];
    const modulesKey = candidates.filter(m => canSee(m) && LOADERS[m]).join(',');

    const [tiles, setTiles] = useState<KpiTile[] | null>(null);

    useEffect(() => {
        let live = true;
        setTiles(null);
        const full = scope !== 'home';
        const list = modulesKey ? (modulesKey.split(',') as ModuleKey[]) : [];
        Promise.allSettled(list.map(m => LOADERS[m]!(institutionId, t, full && m === scope, !full))).then(results => {
            if (!live) return;
            const built = results.flatMap(r => (r.status === 'fulfilled' ? r.value : []));
            let out: KpiTile[];
            if (scope === 'home') {
                out = built.filter(b => b.home)
                    .sort((a, b) => HOME_ORDER.indexOf(a.tile.key) - HOME_ORDER.indexOf(b.tile.key))
                    .slice(0, HOME_MAX).map(b => b.tile);
            } else if (scope === 'people') {
                out = built.map(b => b.tile).filter(k => PEOPLE_ORDER.includes(k.key))
                    .sort((a, b) => PEOPLE_ORDER.indexOf(a.key) - PEOPLE_ORDER.indexOf(b.key));
            } else {
                out = built.map(b => b.tile);
            }
            setTiles(out);
        });
        return () => { live = false; };
        // `t` changes with the language; i18n.language is the stable signal to rebuild the labels.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId, scope, modulesKey, i18n.language]);

    if (!modulesKey) return null;
    if (tiles !== null && tiles.length === 0) return null;

    return (
        <Paper variant="outlined" sx={{ p: 1, mb: 2, minWidth: 0 }} data-testid="board-kpi-strip">
            {tiles === null ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={20} /></Box>
            ) : (
                <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap' }}>
                    {tiles.map(k => (
                        <StatTile key={k.key} label={k.label} unit={k.unit} value={k.value} sub={k.sub} tone={k.tone} />
                    ))}
                </Stack>
            )}
        </Paper>
    );
}
